<template>
  <b-sidebar
    id="add-new-brand-sidebar"
    :visible="isAddNewBrandSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @change="(val) => $emit('update:is-add-new-brand-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">
          {{ formTitle }}
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />

      </div>

      <!-- BODY -->
      <validation-observer
        #default="{ handleSubmit, invalid }"
        ref="refFormObserver"
      >
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
        >

          <!-- Brand Name -->
          <validation-provider
            #default="validationContext"
            name="Brand name"
            rules="required"
          >
            <b-form-group
              label="Brand name *"
              label-for="brand-name"
            >
              <vue-autosuggest
                v-model="brand.name"
                :suggestions="nameOptions"
                :input-props="{id:'autosuggest__input', class:'form-control', placeholder:'', readonly: !newBrand}"
                :state="getValidationState(validationContext)"
                @input="onInputChange"
                @focus="onFocus"
                @selected="onSelected"
              >
                <template slot-scope="{suggestion}">
                  <span>{{ suggestion.item.name }}</span>
                </template>
              </vue-autosuggest>
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <template
            v-if="!newBrand"
          >
            <!-- First Name -->
            <validation-provider
              #default="validationContext"
              name="First name"
              rules=""
            >
              <b-form-group
                label="First name"
                label-for="first-name"
              >
                <b-form-input
                  id="first-name"
                  v-model="brand.user.first_name"
                  autofocus
                  :state="getValidationState(validationContext)"
                  trim
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Last name -->
            <validation-provider
              #default="validationContext"
              name="Last name"
              rules=""
            >
              <b-form-group
                label="Last name"
                label-for="last-name"
              >
                <b-form-input
                  id="last-name"
                  v-model="brand.user.last_name"
                  :state="getValidationState(validationContext)"
                  trim
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Email -->
            <validation-provider
              #default="validationContext"
              name="Email"
              rules="required|email"
            >
              <b-form-group
                label="Email *"
                label-for="email"
              >
                <b-form-input
                  id="email"
                  v-model="brand.user.email"
                  :state="getValidationState(validationContext)"
                  trim
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Job Description -->
            <b-form-group
              label="Job description"
              label-for="job-description"
            >
              <b-form-input
                id="job-description"
                v-model="brand.user.job_description"
              />
            </b-form-group>

            <!-- Send reset password email -->
            <validation-provider
              #default="validationContext"
              name="Send invitation"
              rules=""
            >
              <b-form-group
                label="Send invitation"
                label-for="send-invitation"
              >
                <b-form-checkbox
                  id="send-invitation"
                  v-model="sendInvitation"
                  value="true"
                  unchecked-value="false"
                >Send invitation email
                </b-form-checkbox>

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </template>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2"
              type="submit"
              :disabled="invalid"
            >
              Add
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
            >
              Cancel
            </b-button>
          </div>

        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import axios from '@axios'
import {
  BSidebar, BForm, BFormCheckbox, BFormGroup, BFormInput, BFormInvalidFeedback, BButton,
} from 'bootstrap-vue'
import { ref, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, email } from '@validations'
import formValidation from '@core/comp-functions/forms/form-validation'
import { VueAutosuggest } from 'vue-autosuggest'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BSidebar,
    BForm,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
    VueAutosuggest,
  },
  directives: {
    Ripple,
  },
  props: {
    isAddNewBrandSidebarActive: {
      type: Boolean,
      required: true,
    },
    formTitle: {
      type: String,
      default: 'Add New Brand',
    },
    brandName: {
      type: String,
      default: '',
    },
    newBrand: {
      type: Boolean,
      default: true,
    },
    brandsData: {
      type: [Object, Array],
      default: Array,
    },
  },
  data() {
    return {
      email,
      required,
    }
  },
  setup(props, { emit }) {
    const toast = useToast()

    const brandData = {
      name: '',
      user: {
        first_name: '',
        last_name: '',
        email: '',
        job_description: '',
      },
    }

    const brand = ref(JSON.parse(JSON.stringify(brandData)))
    const nameOptions = ref([])
    const sendInvitation = ref(true)

    watch(() => props.brandName, newValue => {
      if (newValue !== undefined) {
        brand.value.name = newValue
      }
    })

    const resetBrandData = () => {
      brand.value = JSON.parse(JSON.stringify(brandData))
    }

    const {
      refFormObserver,
      getValidationState,
      resetForm,
    } = formValidation(resetBrandData)

    const onFocus = () => {
      if (!props.newBrand) {
        nameOptions.value = []
      }
    }
    const onSelected = option => {
      brand.value.name = option.item.name
    }
    const onInputChange = text => {
      if (text === '' || text === undefined) {
        nameOptions.value = []
        return
      }
      if (props.brandsData) {
        nameOptions.value = [{
          data: props.brandsData.filter(item => item.name.toLowerCase()
            .indexOf(text.toLowerCase()) > -1).slice(0, 10),
        }]
      }
    }
    const onSubmit = () => {
      axios.post(`/api/v1/brands/${props.newBrand ? '?new' : ''}`, brand.value)
        .then(() => {
          toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Brand added',
              icon: 'UserIcon',
              variant: 'success',
            },
          })
          emit('update:is-add-new-brand-sidebar-active', false)
          if (!props.newBrand && sendInvitation.value) {
            axios
              .post('/api/v1/users/reset_password/', {
                email: brand.value.user.email,
                invite: true,
              })
          }
          emit('refetch-data')
          resetForm()
        })
        .catch(error => {
          const {
            email: emailError,
            message,
          } = error.response.data

          if (emailError) {
            toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Email already exists',
                icon: 'EmailIcon',
                variant: 'danger',
              },
            })
          }
          if (message) {
            toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: message,
                icon: 'InfoIcon',
                variant: 'danger',
              },
            })
          }
        })
    }
    return {
      brand,
      nameOptions,
      sendInvitation,
      onFocus,
      onSelected,
      onInputChange,
      onSubmit,

      refFormObserver,
      getValidationState,
      resetForm,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-autosuggest.scss';

#add-new-brand-sidebar {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}
</style>
